import { useEffect, useRef, useState } from "react";

import Recorder from 'recorder-js';

import documentsService from "../../domain/services/solicitation/documents.service";
import loginService from "../../domain/services/login.service";
import clientLogger from "../../domain/services/logging/client.logger";
import useTranscribeAudioDocument from "./useTranscribeAudioDocument";

// Um WAV PCM gerado pelo recorder-js sem samples contém apenas o header RIFF
// de 44 bytes. Um blob menor ou igual a isso não tem áudio real — é artefato
// técnico da gravação, não escolha do cliente.
const WAV_HEADER_ONLY_BYTES = 44;

const DEFAULT_MESSAGES = {
    microphoneDenied:
        'Seu navegador está bloqueando o acesso ao microfone. Clique no ícone de cadeado ao lado do endereço desta página, marque "Permitir" para Microfone e tente gravar novamente.',
    microphoneMissing:
        'Não encontramos um microfone no seu dispositivo. Conecte um microfone (fone com mic, headset ou microfone USB) e tente gravar novamente.',
    microphoneMuted:
        'Seu microfone está mutado. Verifique o botão de mudo no seu fone ou headset e o ícone de som no seu sistema (Windows: ícone do alto-falante na barra; Mac: barra de menus).',
    microphoneInactive:
        'Seu microfone foi reconhecido, mas não está disponível no momento. Outro aplicativo pode estar usando (ex.: Zoom, Teams, Meet) — feche essas janelas e tente novamente.',
    audioContextSuspended:
        'A gravação foi pausada pelo navegador. Clique no botão de gravar novamente para retomar.',
    startFailed:
        'Não foi possível iniciar a gravação. Tente novamente ou digite sua resposta no campo.',
    noSamplesRecorded:
        'A gravação não captou som algum. Verifique se o microfone correto está selecionado nas configurações do seu sistema e se o volume de entrada não está em zero. Se você usa fone com mic, confirme se o mic não está no modo mudo.',
    uploadFailed:
        'Não foi possível enviar sua gravação. Tente novamente ou digite sua resposta no campo.',
    recoverFailed:
        'Não foi possível recuperar o áudio gravado. Tente novamente.',
    missingContext:
        'Salve o pedido antes de gravar o áudio para este campo.'
};

const classifyMediaError = (err) => {
    const name = err && (err.name || err.code);
    if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError')
        return { key: 'microphoneDenied' };
    if (name === 'NotFoundError' || name === 'OverconstrainedError' || name === 'DevicesNotFoundError')
        return { key: 'microphoneMissing' };
    if (name === 'NotReadableError' || name === 'TrackStartError')
        return { key: 'microphoneInactive' };
    return { key: 'startFailed' };
};

// Detecta problemas no stream logo após o getUserMedia resolver, antes de iniciar
// a gravação. Retorna a chave da mensagem didática apropriada, ou null se o stream
// está saudável. Cobre: mic sem tracks, track mutada de fábrica, track em estado
// não-vivo, AudioContext suspenso pelo navegador.
const detectStreamIssue = (stream, audioContext) => {
    const tracks = stream?.getAudioTracks?.() || [];
    if (tracks.length === 0)
        return 'microphoneMissing';
    const track = tracks[0];
    if (track.muted)
        return 'microphoneMuted';
    if (track.readyState && track.readyState !== 'live')
        return 'microphoneInactive';
    if (audioContext?.state === 'suspended')
        return 'audioContextSuspended';
    return null;
};

const useRequestAudioRecorder = (requestId, field) => {

    const [showRecordButton, setShowRecordButton] = useState(false);

    const [recordingState, setRecordingState] = useState('idle');
    const [uploadedAudio, setUploadedAudio] = useState(null);

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const { transcribe, isTranscribing } = useTranscribeAudioDocument();

    const recorder = useRef(null);
    const audioStream = useRef(null);

    useEffect(() => { checkShowRecordButton() }, []);

    useEffect(() => {

        if (!requestId || !field?.id)
            return;

        findFieldAudio(true);
    }, [requestId]);

    const checkShowRecordButton = async () => {

        const customerCanRecordAudio = loginService.getUserAuthentication().customer?.canRecordAudio;
        if (!field.acceptAudioAnswer || !customerCanRecordAudio)
            return;

        setShowRecordButton(true);
    }

    const reportError = (context, message, payload, error) => {
        setErrorMessage(message);
        clientLogger.logClientError({
            context: `audio:${context}`,
            message,
            payload: {
                requestId,
                fieldId: field?.id,
                fieldName: field?.name,
                recordingState,
                ...payload
            },
            error
        });
    };

    const findFieldAudio = async (withoutErrorMessage) =>
        documentsService
            .findByRequestIdAndFieldId(requestId, field.id)
            .then((audio) => setUploadedAudio(audio || null))
            .catch((err) => {
                if (withoutErrorMessage) return;
                reportError('find-field-audio', DEFAULT_MESSAGES.recoverFailed, {}, err);
            });

    const releaseAudioStream = () => {
        try {
            audioStream.current?.getTracks?.().forEach(track => track.stop());
        } catch (_) { /* noop */ }
        audioStream.current = null;
    };

    const startRecording = async () => {

        setErrorMessage(null);

        if (!requestId || !field?.id) {
            reportError(
                'start:missing-context',
                DEFAULT_MESSAGES.missingContext,
                { hasRequestId: !!requestId, hasFieldId: !!field?.id }
            );
            return;
        }

        try {

            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const newRecorder = new Recorder(audioContext);

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            const preflightKey = detectStreamIssue(stream, audioContext);
            if (preflightKey) {
                stream.getTracks().forEach(t => t.stop());
                reportError(
                    `start:${preflightKey}`,
                    DEFAULT_MESSAGES[preflightKey],
                    { tracks: stream.getAudioTracks().length, ctxState: audioContext.state }
                );
                return;
            }

            await newRecorder.init(stream);
            await newRecorder.start();

            const track = stream.getAudioTracks()[0];
            if (track) {
                track.onmute = () => {
                    reportError('recording:track-muted-midflight', DEFAULT_MESSAGES.microphoneMuted, {});
                    setRecordingState('idle');
                    setIsLoading(false);
                    releaseAudioStream();
                };
            }

            audioStream.current = stream;
            recorder.current = newRecorder;

            setRecordingState('recording');
        } catch (err) {
            const { key } = classifyMediaError(err);
            reportError(
                `start:${key}`,
                DEFAULT_MESSAGES[key],
                { errorName: err?.name, errorCode: err?.code },
                err
            );
            releaseAudioStream();
        }
    }

    const handleStopRecording = async () => {

        const isRecording = (recordingState === 'recording');
        if (!isRecording)
            return;

        setIsLoading(true);
        setRecordingState('stopped');

        let blob = null;
        try {
            const result = await recorder.current.stop();
            blob = result?.blob || null;
        } catch (err) {
            reportError('stop:recorder-failed', DEFAULT_MESSAGES.uploadFailed, {}, err);
            releaseAudioStream();
            setIsLoading(false);
            setRecordingState('idle');
            return;
        }

        releaseAudioStream();

        if (!blob || blob.size <= WAV_HEADER_ONLY_BYTES) {
            reportError(
                'stop:empty-blob',
                DEFAULT_MESSAGES.noSamplesRecorded,
                { blobSize: blob?.size ?? null }
            );
            setIsLoading(false);
            setRecordingState('idle');
            return;
        }

        try {
            await handleUploadAudio(blob, 'wav');
        } catch (err) {
            reportError(
                'stop:upload-failed',
                DEFAULT_MESSAGES.uploadFailed,
                { blobSize: blob.size, httpStatus: err?.status },
                err
            );
            setRecordingState('idle');
        } finally {
            setIsLoading(false);
        }
    };

    const extractUploadedDocumentId = (uploadResult) =>
        (uploadResult || []).find(item => item?.document?.id)?.document?.id || null;

    const handleUploadAudio = async (audioBlob, extension) => {

        audioBlob.name = `${field.name}-${requestId}.${extension}`;
        audioBlob.fieldId = field.id;

        const uploadResult = await documentsService.saveAll(requestId, [audioBlob], false);

        await findFieldAudio();

        const uploadedDocumentId = extractUploadedDocumentId(uploadResult);
        transcribe(requestId, uploadedDocumentId)
            .then(findFieldAudio)
            .catch(() => null);
    };

    const handleRemoveAudio = async () => {
        setIsLoading(true);
        return documentsService
            .remove(uploadedAudio)
            .then(() => {
                setRecordingState('idle');
                setUploadedAudio(null);
                setErrorMessage(null);
            })
            .catch((err) => reportError('remove:failed', DEFAULT_MESSAGES.uploadFailed, {}, err))
            .finally(() => setIsLoading(false));
    }

    return {
        showRecordButton,
        showErrorMessage: !!errorMessage,
        errorMessage,
        showAudioRecorder: uploadedAudio || recordingState === 'recording',
        handleStartRecording: startRecording,
        uploadedAudio,
        recordingState,
        isLoading,
        isTranscribing,
        handleStopRecording,
        audioUrl: uploadedAudio?.url,
        handleRemoveAudio
    }
}

export default useRequestAudioRecorder;
